/* eslint-disable jira/naming/disallow-filenames */
// eslint-disable-next-line jira/filename-case
import React, { type ComponentType } from 'react';
// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import {
	createStore,
	createHook,
	createActionsHook,
	createSelector,
	createStateHook,
	type Action,
	type BoundActions,
	type HookActionsFunction,
	type HookFunction,
	type HookStateFunction,
} from '@atlassian/react-sweet-state';
import type { OutputSelector } from '@atlassian/react-sweet-state/src/utils/create-selector.tsx';
import type {
	ActionAnalytics,
	ActiveRegistry,
	CommandPaletteRegistry,
	CommandPaletteRegistryData,
	State,
} from '../../common/types/index.tsx';
import {
	type getAnalyticsAttributesFromState,
	getQueryHash,
	getWordCount,
	getExpandedCommandRegistryAnalytics,
	getRegistryData,
} from '../../common/utils.tsx';

import type {
	AnalyticsAttributes,
	AnalyticsHandler,
	AnalyticsErrorHandler,
} from '../../common/types/analytics/index.tsx';
import type {
	Command,
	CommandInternal,
	ChildResult,
	CommandSections,
	DefaultFallback,
} from '../../common/types/commands/index.tsx';
import { actions, type Actions } from './actions.tsx';

const initialState: State = {
	commandPaletteSessionId: '',
	isOpen: false,
	isLoaded: false,
	search: '',
	activeIndex: 0,
	isSearchFieldFocused: false,
	commands: [],
	parentCommand: [],
	sections: [],
	isDisabled: false,

	registry: {},
	activeRegistry: {
		commandRegistry: {},
		registryIds: [],
	},
	onClosePendingCommandAction: undefined,

	isLoadChildrenError: false,
};

const store = createStore<State, Actions>({
	initialState,
	actions,
	name: 'commandPalette',
});

export const useCommandPalette: HookFunction<
	State,
	BoundActions<
		State,
		{
			readonly open: () => Action<State>;
			readonly toggle: () => Action<State>;
			readonly close: () => Action<State>;
			readonly addCommands: (commands: Command[]) => Action<State>;
			readonly removeCommands: (commands: Command[]) => Action<State>;
			readonly handleSearch: (search: string) => Action<State>;
			readonly forceFocusSearch: (forceSearchFocus?: boolean) => Action<State>;
			readonly setActiveIndex: (index: number) => Action<State>;
			readonly executeCommand: (
				command: CommandInternal,
				analytics?: ActionAnalytics,
				wasSecondaryTriggered?: boolean,
			) => Action<State>;
			readonly goBackLevel: (goToRoot?: boolean, analytics?: ActionAnalytics) => Action<State>;
			readonly setChildCommands: (childResult: ChildResult) => Action<State>;
			readonly setLoadingList: (loadingList: boolean) => Action<State>;
			readonly setFocusInput: (isSearchFieldFocused: boolean) => Action<State>;
			readonly registerSections: (newSections: CommandSections) => Action<State>;
			readonly unregisterSections: (newSections: CommandSections) => Action<State>;
			readonly markAsLoaded: () => Action<State>;
			readonly getAnalyticsAttributes: (
				command: CommandInternal,
				method?: 'mouse' | 'keyboard' | 'mode',
				wasSecondaryTriggered?: boolean,
				attributes?: AnalyticsAttributes,
			) => Action<State, void, ReturnType<typeof getAnalyticsAttributesFromState>>;
			readonly setIsDisabled: (isDisabled: boolean) => Action<State>;
			readonly setRegistry: (registry: CommandPaletteRegistry) => Action<State>;
			readonly updateActiveRegistry: (registryId: string, commands: Command[]) => Action<State>;
			readonly removeCommandsById: (commandIds: string[]) => Action<State>;
			readonly removeFromActiveRegistry: (registryId: string, commands: Command[]) => Action<State>;
			readonly performDialogCloseCommand: () => Action<State>;
			readonly setAnalytics: (
				onAnalytics?: AnalyticsHandler,
				onErrorAnalytics?: AnalyticsErrorHandler,
			) => Action<State>;
			readonly setFallbackOverride: (fallbackOverride?: DefaultFallback) => Action<State>;
			readonly setHasPopupOverlay: (hasPopupOverlay: boolean) => Action<State>;
			readonly forceSearchValue: (search: string) => Action<State>;
			readonly resetShouldForceSearchValue: () => Action<State>;
		}
	>,
	void
> = createHook(store);
export const useActionsCommandPalette: HookActionsFunction<
	BoundActions<
		State,
		{
			readonly open: () => Action<State>;
			readonly toggle: () => Action<State>;
			readonly close: () => Action<State>;
			readonly addCommands: (commands: Command[]) => Action<State>;
			readonly removeCommands: (commands: Command[]) => Action<State>;
			readonly handleSearch: (search: string) => Action<State>;
			readonly forceFocusSearch: (forceSearchFocus?: boolean) => Action<State>;
			readonly setActiveIndex: (index: number) => Action<State>;
			readonly executeCommand: (
				command: CommandInternal,
				analytics?: ActionAnalytics,
				wasSecondaryTriggered?: boolean,
			) => Action<State>;
			readonly goBackLevel: (goToRoot?: boolean, analytics?: ActionAnalytics) => Action<State>;
			readonly setChildCommands: (childResult: ChildResult) => Action<State>;
			readonly setLoadingList: (loadingList: boolean) => Action<State>;
			readonly setFocusInput: (isSearchFieldFocused: boolean) => Action<State>;
			readonly registerSections: (newSections: CommandSections) => Action<State>;
			readonly unregisterSections: (newSections: CommandSections) => Action<State>;
			readonly markAsLoaded: () => Action<State>;
			readonly getAnalyticsAttributes: (
				command: CommandInternal,
				method?: 'mouse' | 'keyboard' | 'mode',
				wasSecondaryTriggered?: boolean,
				attributes?: AnalyticsAttributes,
			) => Action<State, void, ReturnType<typeof getAnalyticsAttributesFromState>>;
			readonly setIsDisabled: (isDisabled: boolean) => Action<State>;
			readonly setRegistry: (registry: CommandPaletteRegistry) => Action<State>;
			readonly updateActiveRegistry: (registryId: string, commands: Command[]) => Action<State>;
			readonly removeCommandsById: (commandIds: string[]) => Action<State>;
			readonly removeFromActiveRegistry: (registryId: string, commands: Command[]) => Action<State>;
			readonly performDialogCloseCommand: () => Action<State>;
			readonly setAnalytics: (
				onAnalytics?: AnalyticsHandler,
				onErrorAnalytics?: AnalyticsErrorHandler,
			) => Action<State>;
			readonly setFallbackOverride: (fallbackOverride?: DefaultFallback) => Action<State>;
			readonly setHasPopupOverlay: (hasPopupOverlay: boolean) => Action<State>;
			readonly forceSearchValue: (search: string) => Action<State>;
			readonly resetShouldForceSearchValue: () => Action<State>;
		}
	>
> = createActionsHook(store);

export const withCommandPalette =
	<Props,>(ComponentToRender: ComponentType<Props>) =>
	(props: Props): React.JSX.Element => {
		const commandPalette = useCommandPalette();

		return <ComponentToRender {...props} commandPalette={commandPalette} />;
	};

// exported for testing
export const commandPaletteAnalyticsAttributesSelector = ({
	search,
	commandPaletteSessionId,
	expandedCommand,
	registry,
	activeRegistry,
}: State): {
	activeRegistryIds: string[];
	activeRegistryIdsLength: number;
	activeRegistryCommandsLength: number;
	relevancyModel: string;
	registryId?: string | undefined;
	teamName?: string | undefined;
	wordCount: number;
	queryLength: number;
	queryHash: string;
	commandPaletteSessionId: string;
	cpMenu: string;
} => ({
	wordCount: getWordCount(search),
	queryLength: search.length,
	queryHash: getQueryHash(search),
	commandPaletteSessionId,
	cpMenu: expandedCommand?.id || 'rootMenu',
	...(getExpandedCommandRegistryAnalytics(registry, activeRegistry, expandedCommand?.id) || {}),
	activeRegistryIds: activeRegistry.registryIds,
	activeRegistryIdsLength: activeRegistry.registryIds.length,
	activeRegistryCommandsLength: Object.keys(activeRegistry.commandRegistry).length,
	relevancyModel: 'v2-string-similarity',
});

export const useCommandPaletteAnalyticsAttributes: HookFunction<
	{
		activeRegistryIds: string[];
		activeRegistryIdsLength: number;
		activeRegistryCommandsLength: number;
		relevancyModel: string;
		registryId?: string | undefined;
		teamName?: string | undefined;
		wordCount: number;
		queryLength: number;
		queryHash: string;
		commandPaletteSessionId: string;
		cpMenu: string;
	},
	BoundActions<
		State,
		{
			readonly open: () => Action<State>;
			readonly toggle: () => Action<State>;
			readonly close: () => Action<State>;
			readonly addCommands: (commands: Command[]) => Action<State>;
			readonly removeCommands: (commands: Command[]) => Action<State>;
			readonly handleSearch: (search: string) => Action<State>;
			readonly forceFocusSearch: (forceSearchFocus?: boolean) => Action<State>;
			readonly setActiveIndex: (index: number) => Action<State>;
			readonly executeCommand: (
				command: CommandInternal,
				analytics?: ActionAnalytics,
				wasSecondaryTriggered?: boolean,
			) => Action<State>;
			readonly goBackLevel: (goToRoot?: boolean, analytics?: ActionAnalytics) => Action<State>;
			readonly setChildCommands: (childResult: ChildResult) => Action<State>;
			readonly setLoadingList: (loadingList: boolean) => Action<State>;
			readonly setFocusInput: (isSearchFieldFocused: boolean) => Action<State>;
			readonly registerSections: (newSections: CommandSections) => Action<State>;
			readonly unregisterSections: (newSections: CommandSections) => Action<State>;
			readonly markAsLoaded: () => Action<State>;
			readonly getAnalyticsAttributes: (
				command: CommandInternal,
				method?: 'mouse' | 'keyboard' | 'mode',
				wasSecondaryTriggered?: boolean,
				attributes?: AnalyticsAttributes,
			) => Action<State, void, ReturnType<typeof getAnalyticsAttributesFromState>>;
			readonly setIsDisabled: (isDisabled: boolean) => Action<State>;
			readonly setRegistry: (registry: CommandPaletteRegistry) => Action<State>;
			readonly updateActiveRegistry: (registryId: string, commands: Command[]) => Action<State>;
			readonly removeCommandsById: (commandIds: string[]) => Action<State>;
			readonly removeFromActiveRegistry: (registryId: string, commands: Command[]) => Action<State>;
			readonly performDialogCloseCommand: () => Action<State>;
			readonly setAnalytics: (
				onAnalytics?: AnalyticsHandler,
				onErrorAnalytics?: AnalyticsErrorHandler,
			) => Action<State>;
			readonly setFallbackOverride: (fallbackOverride?: DefaultFallback) => Action<State>;
			readonly setHasPopupOverlay: (hasPopupOverlay: boolean) => Action<State>;
			readonly forceSearchValue: (search: string) => Action<State>;
			readonly resetShouldForceSearchValue: () => Action<State>;
		}
	>,
	void
> = createHook(store, {
	selector: commandPaletteAnalyticsAttributesSelector,
});

export const useCommandPaletteIsOpen: HookFunction<
	{
		isOpen: boolean;
	},
	BoundActions<
		State,
		{
			readonly open: () => Action<State>;
			readonly toggle: () => Action<State>;
			readonly close: () => Action<State>;
			readonly addCommands: (commands: Command[]) => Action<State>;
			readonly removeCommands: (commands: Command[]) => Action<State>;
			readonly handleSearch: (search: string) => Action<State>;
			readonly forceFocusSearch: (forceSearchFocus?: boolean) => Action<State>;
			readonly setActiveIndex: (index: number) => Action<State>;
			readonly executeCommand: (
				command: CommandInternal,
				analytics?: ActionAnalytics,
				wasSecondaryTriggered?: boolean,
			) => Action<State>;
			readonly goBackLevel: (goToRoot?: boolean, analytics?: ActionAnalytics) => Action<State>;
			readonly setChildCommands: (childResult: ChildResult) => Action<State>;
			readonly setLoadingList: (loadingList: boolean) => Action<State>;
			readonly setFocusInput: (isSearchFieldFocused: boolean) => Action<State>;
			readonly registerSections: (newSections: CommandSections) => Action<State>;
			readonly unregisterSections: (newSections: CommandSections) => Action<State>;
			readonly markAsLoaded: () => Action<State>;
			readonly getAnalyticsAttributes: (
				command: CommandInternal,
				method?: 'mouse' | 'keyboard' | 'mode',
				wasSecondaryTriggered?: boolean,
				attributes?: AnalyticsAttributes,
			) => Action<State, void, ReturnType<typeof getAnalyticsAttributesFromState>>;
			readonly setIsDisabled: (isDisabled: boolean) => Action<State>;
			readonly setRegistry: (registry: CommandPaletteRegistry) => Action<State>;
			readonly updateActiveRegistry: (registryId: string, commands: Command[]) => Action<State>;
			readonly removeCommandsById: (commandIds: string[]) => Action<State>;
			readonly removeFromActiveRegistry: (registryId: string, commands: Command[]) => Action<State>;
			readonly performDialogCloseCommand: () => Action<State>;
			readonly setAnalytics: (
				onAnalytics?: AnalyticsHandler,
				onErrorAnalytics?: AnalyticsErrorHandler,
			) => Action<State>;
			readonly setFallbackOverride: (fallbackOverride?: DefaultFallback) => Action<State>;
			readonly setHasPopupOverlay: (hasPopupOverlay: boolean) => Action<State>;
			readonly forceSearchValue: (search: string) => Action<State>;
			readonly resetShouldForceSearchValue: () => Action<State>;
		}
	>,
	void
> = createHook(store, {
	selector: ({ isOpen }: State) => ({
		isOpen,
	}),
});

export const useCommandPaletteHasChildPopup: HookStateFunction<boolean | undefined, void> =
	createStateHook(store, {
		selector: (state: State) => state.hasPopupOverlay,
	});

export const useCommandPaletteIsLoaded: HookFunction<
	{
		isLoaded: boolean;
	},
	BoundActions<
		State,
		{
			readonly open: () => Action<State>;
			readonly toggle: () => Action<State>;
			readonly close: () => Action<State>;
			readonly addCommands: (commands: Command[]) => Action<State>;
			readonly removeCommands: (commands: Command[]) => Action<State>;
			readonly handleSearch: (search: string) => Action<State>;
			readonly forceFocusSearch: (forceSearchFocus?: boolean) => Action<State>;
			readonly setActiveIndex: (index: number) => Action<State>;
			readonly executeCommand: (
				command: CommandInternal,
				analytics?: ActionAnalytics,
				wasSecondaryTriggered?: boolean,
			) => Action<State>;
			readonly goBackLevel: (goToRoot?: boolean, analytics?: ActionAnalytics) => Action<State>;
			readonly setChildCommands: (childResult: ChildResult) => Action<State>;
			readonly setLoadingList: (loadingList: boolean) => Action<State>;
			readonly setFocusInput: (isSearchFieldFocused: boolean) => Action<State>;
			readonly registerSections: (newSections: CommandSections) => Action<State>;
			readonly unregisterSections: (newSections: CommandSections) => Action<State>;
			readonly markAsLoaded: () => Action<State>;
			readonly getAnalyticsAttributes: (
				command: CommandInternal,
				method?: 'mouse' | 'keyboard' | 'mode',
				wasSecondaryTriggered?: boolean,
				attributes?: AnalyticsAttributes,
			) => Action<State, void, ReturnType<typeof getAnalyticsAttributesFromState>>;
			readonly setIsDisabled: (isDisabled: boolean) => Action<State>;
			readonly setRegistry: (registry: CommandPaletteRegistry) => Action<State>;
			readonly updateActiveRegistry: (registryId: string, commands: Command[]) => Action<State>;
			readonly removeCommandsById: (commandIds: string[]) => Action<State>;
			readonly removeFromActiveRegistry: (registryId: string, commands: Command[]) => Action<State>;
			readonly performDialogCloseCommand: () => Action<State>;
			readonly setAnalytics: (
				onAnalytics?: AnalyticsHandler,
				onErrorAnalytics?: AnalyticsErrorHandler,
			) => Action<State>;
			readonly setFallbackOverride: (fallbackOverride?: DefaultFallback) => Action<State>;
			readonly setHasPopupOverlay: (hasPopupOverlay: boolean) => Action<State>;
			readonly forceSearchValue: (search: string) => Action<State>;
			readonly resetShouldForceSearchValue: () => Action<State>;
		}
	>,
	void
> = createHook(store, {
	selector: ({ isLoaded }: State) => ({
		isLoaded,
	}),
});

export const useCommandPaletteIsDisabled: HookFunction<
	{
		isDisabled: boolean;
	},
	BoundActions<
		State,
		{
			readonly open: () => Action<State>;
			readonly toggle: () => Action<State>;
			readonly close: () => Action<State>;
			readonly addCommands: (commands: Command[]) => Action<State>;
			readonly removeCommands: (commands: Command[]) => Action<State>;
			readonly handleSearch: (search: string) => Action<State>;
			readonly forceFocusSearch: (forceSearchFocus?: boolean) => Action<State>;
			readonly setActiveIndex: (index: number) => Action<State>;
			readonly executeCommand: (
				command: CommandInternal,
				analytics?: ActionAnalytics,
				wasSecondaryTriggered?: boolean,
			) => Action<State>;
			readonly goBackLevel: (goToRoot?: boolean, analytics?: ActionAnalytics) => Action<State>;
			readonly setChildCommands: (childResult: ChildResult) => Action<State>;
			readonly setLoadingList: (loadingList: boolean) => Action<State>;
			readonly setFocusInput: (isSearchFieldFocused: boolean) => Action<State>;
			readonly registerSections: (newSections: CommandSections) => Action<State>;
			readonly unregisterSections: (newSections: CommandSections) => Action<State>;
			readonly markAsLoaded: () => Action<State>;
			readonly getAnalyticsAttributes: (
				command: CommandInternal,
				method?: 'mouse' | 'keyboard' | 'mode',
				wasSecondaryTriggered?: boolean,
				attributes?: AnalyticsAttributes,
			) => Action<State, void, ReturnType<typeof getAnalyticsAttributesFromState>>;
			readonly setIsDisabled: (isDisabled: boolean) => Action<State>;
			readonly setRegistry: (registry: CommandPaletteRegistry) => Action<State>;
			readonly updateActiveRegistry: (registryId: string, commands: Command[]) => Action<State>;
			readonly removeCommandsById: (commandIds: string[]) => Action<State>;
			readonly removeFromActiveRegistry: (registryId: string, commands: Command[]) => Action<State>;
			readonly performDialogCloseCommand: () => Action<State>;
			readonly setAnalytics: (
				onAnalytics?: AnalyticsHandler,
				onErrorAnalytics?: AnalyticsErrorHandler,
			) => Action<State>;
			readonly setFallbackOverride: (fallbackOverride?: DefaultFallback) => Action<State>;
			readonly setHasPopupOverlay: (hasPopupOverlay: boolean) => Action<State>;
			readonly forceSearchValue: (search: string) => Action<State>;
			readonly resetShouldForceSearchValue: () => Action<State>;
		}
	>,
	void
> = createHook(store, {
	selector: ({ isDisabled }: State) => ({
		isDisabled,
	}),
});

export const useCommandPaletteSessionId: HookFunction<
	{
		commandPaletteSessionId: string;
	},
	BoundActions<
		State,
		{
			readonly open: () => Action<State>;
			readonly toggle: () => Action<State>;
			readonly close: () => Action<State>;
			readonly addCommands: (commands: Command[]) => Action<State>;
			readonly removeCommands: (commands: Command[]) => Action<State>;
			readonly handleSearch: (search: string) => Action<State>;
			readonly forceFocusSearch: (forceSearchFocus?: boolean) => Action<State>;
			readonly setActiveIndex: (index: number) => Action<State>;
			readonly executeCommand: (
				command: CommandInternal,
				analytics?: ActionAnalytics,
				wasSecondaryTriggered?: boolean,
			) => Action<State>;
			readonly goBackLevel: (goToRoot?: boolean, analytics?: ActionAnalytics) => Action<State>;
			readonly setChildCommands: (childResult: ChildResult) => Action<State>;
			readonly setLoadingList: (loadingList: boolean) => Action<State>;
			readonly setFocusInput: (isSearchFieldFocused: boolean) => Action<State>;
			readonly registerSections: (newSections: CommandSections) => Action<State>;
			readonly unregisterSections: (newSections: CommandSections) => Action<State>;
			readonly markAsLoaded: () => Action<State>;
			readonly getAnalyticsAttributes: (
				command: CommandInternal,
				method?: 'mouse' | 'keyboard' | 'mode',
				wasSecondaryTriggered?: boolean,
				attributes?: AnalyticsAttributes,
			) => Action<State, void, ReturnType<typeof getAnalyticsAttributesFromState>>;
			readonly setIsDisabled: (isDisabled: boolean) => Action<State>;
			readonly setRegistry: (registry: CommandPaletteRegistry) => Action<State>;
			readonly updateActiveRegistry: (registryId: string, commands: Command[]) => Action<State>;
			readonly removeCommandsById: (commandIds: string[]) => Action<State>;
			readonly removeFromActiveRegistry: (registryId: string, commands: Command[]) => Action<State>;
			readonly performDialogCloseCommand: () => Action<State>;
			readonly setAnalytics: (
				onAnalytics?: AnalyticsHandler,
				onErrorAnalytics?: AnalyticsErrorHandler,
			) => Action<State>;
			readonly setFallbackOverride: (fallbackOverride?: DefaultFallback) => Action<State>;
			readonly setHasPopupOverlay: (hasPopupOverlay: boolean) => Action<State>;
			readonly forceSearchValue: (search: string) => Action<State>;
			readonly resetShouldForceSearchValue: () => Action<State>;
		}
	>,
	void
> = createHook(store, {
	selector: ({ commandPaletteSessionId }: State) => ({
		commandPaletteSessionId,
	}),
});

export const useCommandPaletteExpandedCommand: HookFunction<
	{
		expandedCommand: CommandInternal | undefined;
		isOpen: boolean;
	},
	BoundActions<
		State,
		{
			readonly open: () => Action<State>;
			readonly toggle: () => Action<State>;
			readonly close: () => Action<State>;
			readonly addCommands: (commands: Command[]) => Action<State>;
			readonly removeCommands: (commands: Command[]) => Action<State>;
			readonly handleSearch: (search: string) => Action<State>;
			readonly forceFocusSearch: (forceSearchFocus?: boolean) => Action<State>;
			readonly setActiveIndex: (index: number) => Action<State>;
			readonly executeCommand: (
				command: CommandInternal,
				analytics?: ActionAnalytics,
				wasSecondaryTriggered?: boolean,
			) => Action<State>;
			readonly goBackLevel: (goToRoot?: boolean, analytics?: ActionAnalytics) => Action<State>;
			readonly setChildCommands: (childResult: ChildResult) => Action<State>;
			readonly setLoadingList: (loadingList: boolean) => Action<State>;
			readonly setFocusInput: (isSearchFieldFocused: boolean) => Action<State>;
			readonly registerSections: (newSections: CommandSections) => Action<State>;
			readonly unregisterSections: (newSections: CommandSections) => Action<State>;
			readonly markAsLoaded: () => Action<State>;
			readonly getAnalyticsAttributes: (
				command: CommandInternal,
				method?: 'mouse' | 'keyboard' | 'mode',
				wasSecondaryTriggered?: boolean,
				attributes?: AnalyticsAttributes,
			) => Action<State, void, ReturnType<typeof getAnalyticsAttributesFromState>>;
			readonly setIsDisabled: (isDisabled: boolean) => Action<State>;
			readonly setRegistry: (registry: CommandPaletteRegistry) => Action<State>;
			readonly updateActiveRegistry: (registryId: string, commands: Command[]) => Action<State>;
			readonly removeCommandsById: (commandIds: string[]) => Action<State>;
			readonly removeFromActiveRegistry: (registryId: string, commands: Command[]) => Action<State>;
			readonly performDialogCloseCommand: () => Action<State>;
			readonly setAnalytics: (
				onAnalytics?: AnalyticsHandler,
				onErrorAnalytics?: AnalyticsErrorHandler,
			) => Action<State>;
			readonly setFallbackOverride: (fallbackOverride?: DefaultFallback) => Action<State>;
			readonly setHasPopupOverlay: (hasPopupOverlay: boolean) => Action<State>;
			readonly forceSearchValue: (search: string) => Action<State>;
			readonly resetShouldForceSearchValue: () => Action<State>;
		}
	>,
	void
> = createHook(store, {
	selector: ({ expandedCommand, isOpen }: State) => ({
		expandedCommand,
		isOpen,
	}),
});

export const useCommandPaletteSearch: HookFunction<
	{
		search: string;
	},
	BoundActions<
		State,
		{
			readonly open: () => Action<State>;
			readonly toggle: () => Action<State>;
			readonly close: () => Action<State>;
			readonly addCommands: (commands: Command[]) => Action<State>;
			readonly removeCommands: (commands: Command[]) => Action<State>;
			readonly handleSearch: (search: string) => Action<State>;
			readonly forceFocusSearch: (forceSearchFocus?: boolean) => Action<State>;
			readonly setActiveIndex: (index: number) => Action<State>;
			readonly executeCommand: (
				command: CommandInternal,
				analytics?: ActionAnalytics,
				wasSecondaryTriggered?: boolean,
			) => Action<State>;
			readonly goBackLevel: (goToRoot?: boolean, analytics?: ActionAnalytics) => Action<State>;
			readonly setChildCommands: (childResult: ChildResult) => Action<State>;
			readonly setLoadingList: (loadingList: boolean) => Action<State>;
			readonly setFocusInput: (isSearchFieldFocused: boolean) => Action<State>;
			readonly registerSections: (newSections: CommandSections) => Action<State>;
			readonly unregisterSections: (newSections: CommandSections) => Action<State>;
			readonly markAsLoaded: () => Action<State>;
			readonly getAnalyticsAttributes: (
				command: CommandInternal,
				method?: 'mouse' | 'keyboard' | 'mode',
				wasSecondaryTriggered?: boolean,
				attributes?: AnalyticsAttributes,
			) => Action<State, void, ReturnType<typeof getAnalyticsAttributesFromState>>;
			readonly setIsDisabled: (isDisabled: boolean) => Action<State>;
			readonly setRegistry: (registry: CommandPaletteRegistry) => Action<State>;
			readonly updateActiveRegistry: (registryId: string, commands: Command[]) => Action<State>;
			readonly removeCommandsById: (commandIds: string[]) => Action<State>;
			readonly removeFromActiveRegistry: (registryId: string, commands: Command[]) => Action<State>;
			readonly performDialogCloseCommand: () => Action<State>;
			readonly setAnalytics: (
				onAnalytics?: AnalyticsHandler,
				onErrorAnalytics?: AnalyticsErrorHandler,
			) => Action<State>;
			readonly setFallbackOverride: (fallbackOverride?: DefaultFallback) => Action<State>;
			readonly setHasPopupOverlay: (hasPopupOverlay: boolean) => Action<State>;
			readonly forceSearchValue: (search: string) => Action<State>;
			readonly resetShouldForceSearchValue: () => Action<State>;
		}
	>,
	void
> = createHook(store, {
	selector: ({ search }: State) => ({ search }),
});

export const getCommandPaletteRegistryExpandedCommandRegistryData: OutputSelector<
	State,
	void,
	| {
			registryData: CommandPaletteRegistryData<string> | undefined;
			analyticsAttributes: CommandPaletteRegistryData<string> | undefined;
	  }
	| undefined
> = createSelector(
	({ registry, activeRegistry, expandedCommand }: State) => ({
		registry,
		activeRegistry,
		expandedCommand,
	}),
	() => undefined,
	({ registry, activeRegistry, expandedCommand }) => {
		if (!expandedCommand) return undefined;

		const registryData = getRegistryData(registry, activeRegistry, expandedCommand.id);
		return {
			registryData,
			analyticsAttributes: registryData,
		};
	},
);
export const useCommandPaletteRegistryCurrentAttribution: HookFunction<
	| {
			registryData: CommandPaletteRegistryData<string> | undefined;
			analyticsAttributes: CommandPaletteRegistryData<string> | undefined;
	  }
	| undefined,
	BoundActions<
		State,
		{
			readonly open: () => Action<State>;
			readonly toggle: () => Action<State>;
			readonly close: () => Action<State>;
			readonly addCommands: (commands: Command[]) => Action<State>;
			readonly removeCommands: (commands: Command[]) => Action<State>;
			readonly handleSearch: (search: string) => Action<State>;
			readonly forceFocusSearch: (forceSearchFocus?: boolean) => Action<State>;
			readonly setActiveIndex: (index: number) => Action<State>;
			readonly executeCommand: (
				command: CommandInternal,
				analytics?: ActionAnalytics,
				wasSecondaryTriggered?: boolean,
			) => Action<State>;
			readonly goBackLevel: (goToRoot?: boolean, analytics?: ActionAnalytics) => Action<State>;
			readonly setChildCommands: (childResult: ChildResult) => Action<State>;
			readonly setLoadingList: (loadingList: boolean) => Action<State>;
			readonly setFocusInput: (isSearchFieldFocused: boolean) => Action<State>;
			readonly registerSections: (newSections: CommandSections) => Action<State>;
			readonly unregisterSections: (newSections: CommandSections) => Action<State>;
			readonly markAsLoaded: () => Action<State>;
			readonly getAnalyticsAttributes: (
				command: CommandInternal,
				method?: 'mouse' | 'keyboard' | 'mode',
				wasSecondaryTriggered?: boolean,
				attributes?: AnalyticsAttributes,
			) => Action<State, void, ReturnType<typeof getAnalyticsAttributesFromState>>;
			readonly setIsDisabled: (isDisabled: boolean) => Action<State>;
			readonly setRegistry: (registry: CommandPaletteRegistry) => Action<State>;
			readonly updateActiveRegistry: (registryId: string, commands: Command[]) => Action<State>;
			readonly removeCommandsById: (commandIds: string[]) => Action<State>;
			readonly removeFromActiveRegistry: (registryId: string, commands: Command[]) => Action<State>;
			readonly performDialogCloseCommand: () => Action<State>;
			readonly setAnalytics: (
				onAnalytics?: AnalyticsHandler,
				onErrorAnalytics?: AnalyticsErrorHandler,
			) => Action<State>;
			readonly setFallbackOverride: (fallbackOverride?: DefaultFallback) => Action<State>;
			readonly setHasPopupOverlay: (hasPopupOverlay: boolean) => Action<State>;
			readonly forceSearchValue: (search: string) => Action<State>;
			readonly resetShouldForceSearchValue: () => Action<State>;
		}
	>,
	void
> = createHook(store, {
	selector: getCommandPaletteRegistryExpandedCommandRegistryData,
});

export const useCommandPaletteUsePerformModeState: HookFunction<
	{
		commands: Command[];
		search: string;
		expandedCommand: CommandInternal | undefined;
		childResult: ChildResult | undefined;
		registry: Partial<Record<string, CommandPaletteRegistryData<string>>>;
		activeRegistry: ActiveRegistry<string>;
	},
	BoundActions<
		State,
		{
			readonly open: () => Action<State>;
			readonly toggle: () => Action<State>;
			readonly close: () => Action<State>;
			readonly addCommands: (commands: Command[]) => Action<State>;
			readonly removeCommands: (commands: Command[]) => Action<State>;
			readonly handleSearch: (search: string) => Action<State>;
			readonly forceFocusSearch: (forceSearchFocus?: boolean) => Action<State>;
			readonly setActiveIndex: (index: number) => Action<State>;
			readonly executeCommand: (
				command: CommandInternal,
				analytics?: ActionAnalytics,
				wasSecondaryTriggered?: boolean,
			) => Action<State>;
			readonly goBackLevel: (goToRoot?: boolean, analytics?: ActionAnalytics) => Action<State>;
			readonly setChildCommands: (childResult: ChildResult) => Action<State>;
			readonly setLoadingList: (loadingList: boolean) => Action<State>;
			readonly setFocusInput: (isSearchFieldFocused: boolean) => Action<State>;
			readonly registerSections: (newSections: CommandSections) => Action<State>;
			readonly unregisterSections: (newSections: CommandSections) => Action<State>;
			readonly markAsLoaded: () => Action<State>;
			readonly getAnalyticsAttributes: (
				command: CommandInternal,
				method?: 'mouse' | 'keyboard' | 'mode',
				wasSecondaryTriggered?: boolean,
				attributes?: AnalyticsAttributes,
			) => Action<State, void, ReturnType<typeof getAnalyticsAttributesFromState>>;
			readonly setIsDisabled: (isDisabled: boolean) => Action<State>;
			readonly setRegistry: (registry: CommandPaletteRegistry) => Action<State>;
			readonly updateActiveRegistry: (registryId: string, commands: Command[]) => Action<State>;
			readonly removeCommandsById: (commandIds: string[]) => Action<State>;
			readonly removeFromActiveRegistry: (registryId: string, commands: Command[]) => Action<State>;
			readonly performDialogCloseCommand: () => Action<State>;
			readonly setAnalytics: (
				onAnalytics?: AnalyticsHandler,
				onErrorAnalytics?: AnalyticsErrorHandler,
			) => Action<State>;
			readonly setFallbackOverride: (fallbackOverride?: DefaultFallback) => Action<State>;
			readonly setHasPopupOverlay: (hasPopupOverlay: boolean) => Action<State>;
			readonly forceSearchValue: (search: string) => Action<State>;
			readonly resetShouldForceSearchValue: () => Action<State>;
		}
	>,
	void
> = createHook(store, {
	selector: ({
		commands,
		search,
		expandedCommand,
		childResult,
		registry,
		activeRegistry,
	}: State) => ({
		commands,
		search,
		expandedCommand,
		childResult,
		registry,
		activeRegistry,
	}),
});
